import { AxiosError } from 'axios';
import { checklistClient } from '../../config/api';
import {
  Checklist,
  ChecklistApiResponse,
  ChecklistVersionsApiResponse,
  CreateChecklistRequest,
  UpdateChecklistRequest,
  ChecklistVersion,
} from '../types/checklist.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class ChecklistService {
  private readonly basePath = '/api/checklists';

  createChecklist = async (data: CreateChecklistRequest): Promise<Checklist> => {
    try {
      const response = await checklistClient.post<any>(this.basePath, data);

      if (response.data && response.data.id) {
        return response.data as Checklist;
      }

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      if (response.status === 201) {
        return response.data as Checklist;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error creating checklist');
    }
  };

  updateChecklist = async (id: string, data: UpdateChecklistRequest): Promise<Checklist> => {
    try {
      const response = await checklistClient.put<any>(
        `${this.basePath}/${id}`,
        data
      );

      if (response.data && response.data.id) {
        return response.data as Checklist;
      }

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      if (response.status === 200) {
        return response.data as Checklist;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating checklist');
    }
  };

  getChecklistById = async (id: string): Promise<Checklist> => {
    try {
      const response = await checklistClient.get<any>(`${this.basePath}/${id}`);

      if (response.data && response.data.id) {
        return response.data as Checklist;
      }

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching checklist');
    }
  };

  getChecklistByInspectionType = async (inspectionTypeId: string, pageNumber: number = 1, pageSize: number = 10): Promise<{ items: Checklist[], totalCount: number, totalPages: number }> => {
    try {
      const response = await checklistClient.get<any>(
        `${this.basePath}/by-inspection-type/${inspectionTypeId}?pageNumber=${pageNumber}&pageSize=${pageSize}`
      );

      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        return {
          items: response.data.items,
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 1
        };
      }

      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          totalCount: response.data.length,
          totalPages: 1
        };
      }

      if (response.data && typeof response.data === 'object' && 'id' in response.data) {
        return {
          items: [response.data as Checklist],
          totalCount: 1,
          totalPages: 1
        };
      }

      const apiResponse = response.data as any;
      if (apiResponse.success && apiResponse.data) {
        const items = Array.isArray(apiResponse.data) ? apiResponse.data : [apiResponse.data];
        return {
          items,
          totalCount: items.length,
          totalPages: 1
        };
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 404) {
          return { items: [], totalCount: 0, totalPages: 0 };
        }

        if (error.response?.data?.error) {
          return { items: [], totalCount: 0, totalPages: 0 };
        }
      }

      apiErrorHandler(error, 'Error fetching checklist');
    }
  };

  getAllChecklists = async (pageNumber: number = 1, pageSize: number = 10): Promise<{ items: Checklist[], totalCount: number, totalPages: number }> => {
    try {
      const response = await checklistClient.get<any>(`${this.basePath}?pageNumber=${pageNumber}&pageSize=${pageSize}`);

      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        return {
          items: response.data.items,
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 1
        };
      }

      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          totalCount: response.data.length,
          totalPages: 1
        };
      }

      const apiResponse = response.data as any;
      if (apiResponse.success && apiResponse.data) {
        return {
          items: apiResponse.data,
          totalCount: apiResponse.data.length,
          totalPages: 1
        };
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching checklists');
    }
  };

  getActiveChecklists = async (): Promise<Checklist[]> => {
    try {
      const response = await checklistClient.get<Checklist[]>(`${this.basePath}/active`);

      if (Array.isArray(response.data)) {
        return response.data;
      }

      const apiResponse = response.data as any;
      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching active checklists');
    }
  };

  getInactiveChecklists = async (): Promise<Checklist[]> => {
    try {
      const response = await checklistClient.get<Checklist[]>(`${this.basePath}/inactive`);

      if (Array.isArray(response.data)) {
        return response.data;
      }

      const apiResponse = response.data as any;
      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching inactive checklists');
    }
  };

  activateChecklist = async (id: string): Promise<void> => {
    try {
      const response = await checklistClient.patch<any>(
        `${this.basePath}/${id}/activate`
      );

      if (response.data && response.data.success === false) {
        throw new Error(response.data?.errorMessage || 'Error activating checklist');
      }

      return;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error activating checklist');
    }
  };

  deactivateChecklist = async (id: string): Promise<void> => {
    try {
      const response = await checklistClient.patch<any>(
        `${this.basePath}/${id}/deactivate`
      );

      if (response.data && response.data.success === false) {
        throw new Error(response.data?.errorMessage || 'Error deactivating checklist');
      }

      return;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deactivating checklist');
    }
  };

  deleteChecklist = async (id: string): Promise<void> => {
    try {
      const response = await checklistClient.delete<ChecklistApiResponse>(`${this.basePath}/${id}`);

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting checklist');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting checklist');
    }
  };

  getVersionHistory = async (id: string): Promise<ChecklistVersion[]> => {
    try {
      const response = await checklistClient.get<ChecklistVersionsApiResponse>(
        `${this.basePath}/${id}/versions`
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching version history');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching version history');
    }
  };
}

export const checklistService = new ChecklistService();
